/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { TFunction } from "i18next";
import { FieldProps } from "rc-field-form/es/Field";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { FieldChildrenProps, Form } from "../Form";

import "./LabelField.scss";

const { Field } = Form;

const getFieldId = (
  id: string | undefined,
  name: FieldProps["name"],
  fallbackId: string,
) => {
  if (id) {
    return id;
  }

  if (typeof name === "string" || typeof name === "number") {
    return `field-${name}`;
  }

  if (Array.isArray(name)) {
    return `field-${name.join("-")}`;
  }

  return `field-${fallbackId.replace(/:/g, "")}`;
};

interface ErrorProps {
  children: React.ReactNode[];
  translate: TFunction;
  id: string;
}

const Error = ({ children, translate, id }: ErrorProps) => (
  <ul id={id} className="Error" aria-live="polite">
    {children &&
      children.map((error, index: number) => (
        <li key={index}>
          {typeof error === "string" ? translate(error) : error}
        </li>
      ))}
  </ul>
);

interface ILabelFieldProps extends FieldProps {
  errorClass?: string;
  apiErrors?: string[];
  otherErrors?: string[];
  label?: React.ReactNode;
  labelHidden?: boolean;
  tip?: React.ReactNode;
  requiredIcon?: boolean;
  htmlFor?: string;
}

const LabelFieldControl: React.FC<ILabelFieldProps & FieldChildrenProps> = ({
  children,
  control,
  meta,
  form,
  apiErrors,
  otherErrors,
  errorClass,
  label,
  labelHidden,
  name,
  tip,
  requiredIcon,
  htmlFor,
}) => {
  const { t } = useTranslation();
  const fallbackId = React.useId();
  const [visited, setVisited] = useState(control.touched);
  const child = React.isValidElement(children) ? children : undefined;
  const childProps = (child?.props ?? {}) as {
    id?: string;
    "aria-describedby"?: string;
    "aria-invalid"?: boolean | "true" | "false";
    "aria-labelledby"?: string;
  };
  const childId = getFieldId(htmlFor ?? childProps.id, name, fallbackId);
  const labelId = `${childId}-label`;
  const errorId = `${childId}-error`;
  const tipId = `${childId}-tip`;
  const hasErrors = meta.errors.length > 0;
  const hasApiErrors = apiErrors && apiErrors.length > 0;
  const hasOtherErrors = otherErrors && otherErrors.length > 0;
  const errors =
    (hasApiErrors || hasOtherErrors) && !hasErrors
      ? [...meta.errors, ...(apiErrors || []), ...(otherErrors || [])]
      : meta.errors;
  const displayError =
    !control.touched && (hasErrors || hasApiErrors || hasOtherErrors);
  const ariaDescribedBy = [
    childProps["aria-describedby"],
    tip ? tipId : undefined,
    displayError ? errorId : undefined,
  ]
    .filter(Boolean)
    .join(" ");
  const ariaLabelledBy = [
    childProps["aria-labelledby"],
    label ? labelId : undefined,
  ]
    .filter(Boolean)
    .join(" ");

  const onBlur = useCallback(
    (...args: any) => {
      if (control.onBlur) {
        if (!visited) {
          setVisited(true);
        }

        control.onBlur(...args);
      }
    },
    [control, visited],
  );

  if (displayError) {
    control.className = errorClass;
  }

  const controlWithA11y = {
    ...control,
    id: childId,
    onBlur,
    "aria-invalid": displayError || childProps["aria-invalid"] || undefined,
    "aria-describedby": ariaDescribedBy || undefined,
    "aria-labelledby": ariaLabelledBy || undefined,
    "aria-errormessage": displayError ? errorId : undefined,
    "aria-required": requiredIcon || undefined,
  };

  const childNode =
    typeof children === "function"
      ? children(controlWithA11y, { ...meta, errors }, form)
      : React.cloneElement(children as React.ReactElement, {
          ...controlWithA11y,
        });

  return (
    <div className="LabelField">
      <div className={labelHidden ? "Label sr-only" : "Label"}>
        <label id={labelId} htmlFor={childId}>
          {label} {requiredIcon && <span className="required">*</span>}
        </label>
        {tip && <span id={tipId}>{tip}</span>}
      </div>
      {childNode}
      {displayError && (
        <Error id={errorId} translate={t}>
          {errors}
        </Error>
      )}
    </div>
  );
};

const DEFAULT_VALIDATE_TRIGGER = ["onChange", "onBlur", "onSubmit"];

const LabelField: React.FunctionComponent<ILabelFieldProps> = ({
  name,
  label,
  labelHidden,
  tip,
  apiErrors,
  errorClass,
  otherErrors,
  children,
  requiredIcon,
  htmlFor,
  ...restProps
}) => {
  return (
    <Field
      name={name}
      validateTrigger={DEFAULT_VALIDATE_TRIGGER}
      {...restProps}
    >
      {(control, meta, form) => (
        <LabelFieldControl
          control={control}
          meta={meta}
          form={form}
          label={label}
          labelHidden={labelHidden}
          tip={tip}
          apiErrors={apiErrors}
          otherErrors={otherErrors}
          errorClass={errorClass}
          name={name}
          requiredIcon={requiredIcon}
          htmlFor={htmlFor}
        >
          {children}
        </LabelFieldControl>
      )}
    </Field>
  );
};

LabelField.defaultProps = {
  errorClass: "FormInputError",
};

export { LabelField };
